import { useEffect, useState } from 'react'
import {
  Box, Checkbox, Dialog, DialogContent, DialogTitle, FormControl,
  FormControlLabel, IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material'
import { Close, Delete } from '@mui/icons-material'
import { Button } from './atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { EnergyStorageDriver } from '../types/ports'
import { ID } from '../types/models'

type Op = 'read' | 'write'
type FuncCode = 3 | 4 | 6 | 16
type AddrMode = 'dec' | 'hex' | 'dec-sungrow'

const TYPES = ['HEX16', 'I16', 'U16', 'B16', 'HEX32', 'I32', 'U32', 'B32', 'ASCII'] as const
type ValueType = (typeof TYPES)[number]

function regsForType(t: ValueType, asciiLen: number): number {
  if (t === 'ASCII') return Math.max(1, Math.ceil(asciiLen / 2))
  if (t.endsWith('32')) return 2
  return 1
}

interface TypeRow {
  type: ValueType
  asciiLen: number // chars; only used if type === 'ASCII'
  value: string // only used in write mode
}

const defaultRow = (): TypeRow => ({ type: 'HEX16', asciiLen: 8, value: '' })

interface Preset {
  name: string
  drivers: EnergyStorageDriver[]
  op: Op
  funcCode: FuncCode
  addrMode: AddrMode
  startAddr: string
  swapEndianness: boolean
  rows: TypeRow[]
}

// Solis S5/S6 only. SOLIS_HYBRID is the S5 driver; SOLIS_S6 is the S6 driver.
const SOLIS_S5_S6: EnergyStorageDriver[] = ['SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID', 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6']

const row = (type: ValueType, asciiLen = 0): TypeRow => ({ type, asciiLen, value: '' })

// Preset: DSP version, HMI version, protocol version, then SN as 32-char ASCII (16 regs)
// matches the -s32 read in fcr-prequalification/read_solis_ser_vers.sh
const PRESETS: Preset[] = [
  {
    name: 'Read Solis serial/versions',
    drivers: ['SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID', 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6', 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_F2'],
    op: 'read',
    funcCode: 4,
    addrMode: 'dec',
    startAddr: '33001',
    swapEndianness: false,
    rows: [
      row('HEX16'),
      row('HEX16'),
      row('HEX16'),
      row('ASCII', 32),
    ],
  },
  {
    name: 'Read Solis BMS charge & discharge limits, 0.1A unit',
    drivers: SOLIS_S5_S6,
    op: 'read',
    funcCode: 4,
    addrMode: 'dec',
    startAddr: '33143',
    swapEndianness: false,
    rows: [row('U16'), row('U16')],
  },
  {
    name: 'Read Solis status, 0x0003 = generating, failure codes start at 0x1000',
    drivers: SOLIS_S5_S6,
    op: 'read',
    funcCode: 4,
    addrMode: 'dec',
    startAddr: '33095',
    swapEndianness: false,
    rows: [row('HEX16')],
  },
  {
    name: 'Read Solis remote dispatch command registers',
    drivers: SOLIS_S5_S6,
    op: 'read',
    funcCode: 3,
    addrMode: 'dec',
    startAddr: '44100',
    swapEndianness: false,
    rows: [
      row('HEX16'),
      row('U16'),
      row('HEX16'),
      row('U16'),
      row('U16'),
      row('U16'),
      row('I32'),
      row('HEX16'),
    ],
  },
]

// Parse what the user typed, in whatever base the address mode uses. Sungrow's -1 wire adjustment
// is not applied here — this is the "displayed" address that we echo back next to each row.
function parseEnteredAddr(mode: AddrMode, text: string): number | null {
  let n: number
  if (mode === 'hex') {
    const clean = text.trim().replace(/^0x/i, '')
    if (!/^[0-9a-f]+$/i.test(clean)) return null
    n = parseInt(clean, 16)
  } else {
    if (!/^-?[0-9]+$/.test(text.trim())) return null
    n = parseInt(text.trim(), 10)
  }
  if (n < 0 || n > 0xFFFF) return null
  return n
}

function parseStartAddrToWire(mode: AddrMode, text: string): number | null {
  const n = parseEnteredAddr(mode, text)
  if (n === null) return null
  const wire = mode === 'dec-sungrow' ? n - 1 : n
  if (wire < 0 || wire > 0xFFFF) return null
  return wire
}

function formatAddr(mode: AddrMode, n: number): string {
  if (mode === 'hex') return '@0x' + n.toString(16).toUpperCase()
  return '@' + n.toString(10)
}

// Interpret reg list against a row list. Returns one display string per row.
function parseReadResult(regs: number[], rows: TypeRow[], swap: boolean): string[] {
  const out: string[] = []
  let i = 0
  for (const row of rows) {
    const n = regsForType(row.type, row.asciiLen)
    const slice = regs.slice(i, i + n)
    if (slice.length < n) { out.push('?'); i += n; continue }
    let words = slice.slice()
    if (swap && words.length >= 2) {
      // Swap 16-bit word order within this slice
      words = words.reverse()
    }
    out.push(interpret(row, words))
    i += n
  }
  return out
}

function interpret(row: TypeRow, words: number[]): string {
  const w0 = words[0] ?? 0
  const w1 = words[1] ?? 0
  switch (row.type) {
    case 'HEX16': return '0x' + w0.toString(16).padStart(4, '0').toUpperCase()
    case 'U16': return w0.toString(10)
    case 'I16': return ((w0 & 0x8000) ? w0 - 0x10000 : w0).toString(10)
    case 'B16': return w0.toString(2).padStart(16, '0')
    case 'HEX32': {
      const u32 = (w0 * 0x10000) + w1
      return '0x' + u32.toString(16).padStart(8, '0').toUpperCase()
    }
    case 'U32': {
      const u32 = (w0 * 0x10000) + w1
      return u32.toString(10)
    }
    case 'I32': {
      const u32 = (w0 * 0x10000) + w1
      const v = u32 >= 0x80000000 ? u32 - 0x100000000 : u32
      return v.toString(10)
    }
    case 'B32': {
      return w0.toString(2).padStart(16, '0') + ' ' + w1.toString(2).padStart(16, '0')
    }
    case 'ASCII': {
      let s = ''
      for (const w of words) {
        s += String.fromCharCode((w >> 8) & 0xFF) + String.fromCharCode(w & 0xFF)
      }
      // Trim trailing nulls and trailing padding chars
      // eslint-disable-next-line no-control-regex
      return s.replace(/ +$/, '').slice(0, row.asciiLen)
    }
  }
}

// Encode a row's user-entered value into u16 registers. Returns null on parse error.
function encodeWrite(row: TypeRow, swap: boolean): number[] | null {
  const v = row.value.trim()
  const out: number[] = []
  switch (row.type) {
    case 'HEX16': {
      const n = parseInt(v.replace(/^0x/i, ''), 16)
      if (isNaN(n) || n < 0 || n > 0xFFFF) return null
      out.push(n)
      break
    }
    case 'U16': {
      const n = parseInt(v, 10)
      if (isNaN(n) || n < 0 || n > 0xFFFF) return null
      out.push(n)
      break
    }
    case 'I16': {
      const n = parseInt(v, 10)
      if (isNaN(n) || n < -0x8000 || n > 0x7FFF) return null
      out.push(n < 0 ? n + 0x10000 : n)
      break
    }
    case 'B16': {
      if (!/^[01]+$/.test(v) || v.length > 16) return null
      out.push(parseInt(v, 2))
      break
    }
    case 'HEX32': {
      const n = parseInt(v.replace(/^0x/i, ''), 16)
      if (isNaN(n) || n < 0 || n > 0xFFFFFFFF) return null
      out.push((n >>> 16) & 0xFFFF, n & 0xFFFF)
      break
    }
    case 'U32': {
      const n = parseInt(v, 10)
      if (isNaN(n) || n < 0 || n > 0xFFFFFFFF) return null
      out.push(Math.floor(n / 0x10000) & 0xFFFF, n & 0xFFFF)
      break
    }
    case 'I32': {
      const n = parseInt(v, 10)
      if (isNaN(n) || n < -0x80000000 || n > 0x7FFFFFFF) return null
      const u = n < 0 ? n + 0x100000000 : n
      out.push(Math.floor(u / 0x10000) & 0xFFFF, u & 0xFFFF)
      break
    }
    case 'B32': {
      const clean = v.replace(/\s+/g, '')
      if (!/^[01]+$/.test(clean) || clean.length > 32) return null
      const n = parseInt(clean, 2)
      out.push(Math.floor(n / 0x10000) & 0xFFFF, n & 0xFFFF)
      break
    }
    case 'ASCII': {
      const n = regsForType(row.type, row.asciiLen)
      // Pad/truncate to 2*n chars
      const padded = (v + ' '.repeat(2 * n)).slice(0, 2 * n)
      for (let i = 0; i < n; i++) {
        const hi = padded.charCodeAt(2 * i) & 0xFF
        const lo = padded.charCodeAt(2 * i + 1) & 0xFF
        out.push((hi << 8) | lo)
      }
      break
    }
  }
  if (swap && out.length >= 2) return out.reverse()
  return out
}

interface HistoryEntry {
  ts: Date
  op: Op
  funcCode: FuncCode
  regaddr: number
  status: 'success' | 'failure' | 'timeout' | 'error'
  parsed: string[]
}

export interface ModbusDialogProps {
  open: boolean
  onClose: () => void
  portId: ID
  driver?: EnergyStorageDriver
  slaveId?: number
  emSlaveId?: number
  hwId: string
}

export function ModbusDialog({ open, onClose, portId, driver, slaveId, emSlaveId, hwId }: ModbusDialogProps) {
  const [allowWrites, setAllowWrites] = useState(false)
  const [op, setOp] = useState<Op>('read')
  const [funcCode, setFuncCode] = useState<FuncCode>(4)
  const [addrMode, setAddrMode] = useState<AddrMode>('dec')
  const [startAddr, setStartAddr] = useState('')
  const [swapEndianness, setSwapEndianness] = useState(false)
  const [useEmSlaveId, setUseEmSlaveId] = useState(false)
  const [rows, setRows] = useState<TypeRow[]>([defaultRow()])
  const [pending, setPending] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [presetIdx, setPresetIdx] = useState<number>(-1)

  // Auto-pick dec-sungrow when driver is Sungrow
  useEffect(() => {
    if (open && driver === 'SPP_ENERGYSTORAGE_SUNGROW_HYBRID') {
      setAddrMode('dec-sungrow')
    }
  }, [open, driver])

  // When op flips, snap funcCode into the valid set
  useEffect(() => {
    if (op === 'read' && (funcCode === 6 || funcCode === 16)) setFuncCode(4)
    if (op === 'write' && (funcCode === 3 || funcCode === 4)) setFuncCode(6)
  }, [op]) // eslint-disable-line react-hooks/exhaustive-deps

  // 0x06 = write single register: force exactly one 16-bit-typed row
  useEffect(() => {
    if (funcCode === 6) {
      setRows((prev) => {
        const r = prev[0] ?? defaultRow()
        const t: ValueType = r.type.endsWith('32') || r.type === 'ASCII' ? 'HEX16' : r.type
        return [{ ...r, type: t, asciiLen: 0 }]
      })
    }
  }, [funcCode])

  const applicablePresets = PRESETS.filter((p) => !driver || p.drivers.includes(driver))
  const showSlaveIdSelector =
    typeof slaveId === 'number' && typeof emSlaveId === 'number' && emSlaveId !== 0 && slaveId !== emSlaveId

  const nRegs = rows.reduce((a, r) => a + regsForType(r.type, r.asciiLen), 0)

  const enteredAddr = parseEnteredAddr(addrMode, startAddr)
  // Precompute per-row displayed addresses (in the user's chosen mode/base)
  const rowAddrs = (() => {
    const out: (string | null)[] = []
    let off = 0
    for (const r of rows) {
      out.push(enteredAddr === null ? null : formatAddr(addrMode, enteredAddr + off))
      off += regsForType(r.type, r.asciiLen)
    }
    return out
  })()

  const onApplyPreset = (idx: number) => {
    setPresetIdx(idx)
    if (idx < 0) return
    const p = applicablePresets[idx]
    if (!p) return
    setOp(p.op)
    setFuncCode(p.funcCode)
    setAddrMode(p.addrMode)
    setStartAddr(p.startAddr)
    setSwapEndianness(p.swapEndianness)
    setRows(p.rows.map((r) => ({ ...r })))
  }

  const wireAddr = parseStartAddrToWire(addrMode, startAddr)
  const startAddrError = startAddr.length > 0 && wireAddr === null

  const canRunRead = op === 'read' && !pending && wireAddr !== null && nRegs >= 1 && nRegs <= 43
  const canRunWrite =
    op === 'write' && allowWrites && !pending && wireAddr !== null && nRegs >= 1 && nRegs <= 43 &&
    rows.every((r) => encodeWrite(r, false) !== null)

  const run = async () => {
    if (wireAddr === null) return
    setPending(true)
    let writeData: number[] = []
    if (op === 'write') {
      for (const r of rows) {
        const enc = encodeWrite(r, false)
        if (!enc) { setPending(false); return }
        writeData = writeData.concat(enc)
      }
      if (swapEndianness && writeData.length >= 2) {
        // Per-32-bit-row swap is more useful, but Solis-style read swaps whole tail; mirror commander's -M.
        // For writes we apply the same convention as reads: swap each 32-bit pair within row, not across rows.
        writeData = []
        for (const r of rows) {
          const enc = encodeWrite(r, true)
          if (!enc) { setPending(false); return }
          writeData = writeData.concat(enc)
        }
      }
    }

    const res = await callAxios<{ status: 'success' | 'failure' | 'timeout', registers: number[] }>(
      { method: 'POST', url: `/ports/${portId}/modbus` },
      {
        command: op === 'read' ? 'ENERGYSTORAGE_CMD_MODBUS_READ' : 'ENERGYSTORAGE_CMD_MODBUS_WRITE',
        regaddr: wireAddr,
        func_code: funcCode,
        n_regs: nRegs,
        use_em_slave_id: useEmSlaveId,
        write_data: writeData,
      },
    )

    const entry: HistoryEntry = {
      ts: new Date(),
      op,
      funcCode,
      regaddr: wireAddr,
      status: 'error',
      parsed: [],
    }

    if (res.success) {
      entry.status = res.data.status
      if (op === 'read' && res.data.status === 'success') {
        entry.parsed = parseReadResult(res.data.registers, rows, swapEndianness)
      } else if (op === 'write' && res.data.status === 'success') {
        entry.parsed = rows.map((r) => `${r.type}=${r.value}`)
      }
    } else {
      entry.status = 'error'
    }

    setHistory((h) => [entry, ...h].slice(0, 10))
    setPending(false)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Modbus console &mdash; {hwId}
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Preset row */}
        {applicablePresets.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 480 }}>
              <InputLabel>Preset</InputLabel>
              <Select
                label="Preset"
                value={presetIdx}
                onChange={(e) => onApplyPreset(Number(e.target.value))}
              >
                <MenuItem value={-1}><em>(none)</em></MenuItem>
                {applicablePresets.map((p, i) => (
                  <MenuItem key={p.name} value={i}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Allow writes */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={allowWrites} onChange={(e) => {
              setAllowWrites(e.target.checked)
              if (!e.target.checked) setOp('read')
            }} />}
            label="Allow modbus writes"
          />
          {allowWrites && (
            <Typography sx={{ color: 'error.main', fontWeight: 700 }}>
              Warning &mdash; wrong modbus writes can damage the inverter! Proceed at your own risk!
            </Typography>
          )}
        </Box>

        {/* Op + func code + addr mode + addr + n_regs + swap */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Operation</InputLabel>
            <Select label="Operation" value={op} onChange={(e) => setOp(e.target.value as Op)}>
              <MenuItem value="read">Read</MenuItem>
              <MenuItem value="write" disabled={!allowWrites}>Write</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel>Function code</InputLabel>
            <Select label="Function code" value={funcCode} onChange={(e) => setFuncCode(Number(e.target.value) as FuncCode)}>
              {op === 'read'
                ? [
                <MenuItem key={3} value={3}>0x03 &mdash; Read holding</MenuItem>,
                <MenuItem key={4} value={4}>0x04 &mdash; Read input</MenuItem>,
                  ]
                : [
                <MenuItem key={6} value={6}>0x06 &mdash; Write single</MenuItem>,
                <MenuItem key={16} value={16}>0x10 &mdash; Write multiple</MenuItem>,
                  ]}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Address mode</InputLabel>
            <Select label="Address mode" value={addrMode} onChange={(e) => setAddrMode(e.target.value as AddrMode)}>
              <MenuItem value="dec">dec</MenuItem>
              <MenuItem value="hex">hex</MenuItem>
              <MenuItem value="dec-sungrow">dec-sungrow</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Start address"
            value={startAddr}
            onChange={(e) => setStartAddr(e.target.value)}
            error={startAddrError}
            sx={{ width: 160 }}
          />

          <FormControlLabel
            control={<Checkbox checked={swapEndianness} onChange={(e) => setSwapEndianness(e.target.checked)} />}
            label="Swap endianness"
          />

          {showSlaveIdSelector && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Slave id</InputLabel>
              <Select
                label="Slave id"
                value={useEmSlaveId ? 'em' : 'main'}
                onChange={(e) => setUseEmSlaveId(e.target.value === 'em')}
              >
                <MenuItem value="main">{slaveId} (main)</MenuItem>
                <MenuItem value="em">{emSlaveId} (energy meter)</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Type rows */}
        <Box sx={{ mb: 2 }}>
          {rows.map((row, i) => {
            const regs = regsForType(row.type, row.asciiLen)
            return (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Typography
                  sx={{ fontFamily: 'monospace', minWidth: 90, color: rowAddrs[i] === null ? 'text.disabled' : 'text.primary' }}
                >
                  {rowAddrs[i] ?? '@?'}
                </Typography>

                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select
                    value={row.type}
                    onChange={(e) => {
                      const t = e.target.value as ValueType
                      setRows((rs) => rs.map((r, j) => j === i ? { ...r, type: t } : r))
                    }}
                  >
                    {TYPES.map((t) => (
                      <MenuItem
                        key={t}
                        value={t}
                        disabled={funcCode === 6 && (t.endsWith('32') || t === 'ASCII')}
                      >
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {row.type === 'ASCII' && (
                  <TextField
                    size="small"
                    type="number"
                    label="ASCII length"
                    value={row.asciiLen}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(86, Number(e.target.value) || 0))
                      setRows((rs) => rs.map((r, j) => j === i ? { ...r, asciiLen: n } : r))
                    }}
                    sx={{ width: 120 }}
                  />
                )}

                {op === 'write' && (
                  <TextField
                    size="small"
                    label="Value"
                    value={row.value}
                    onChange={(e) => setRows((rs) => rs.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                    sx={{ flex: 1 }}
                  />
                )}

                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 60 }}>
                  ({regs} reg{regs === 1 ? '' : 's'})
                </Typography>

                <IconButton
                  size="small"
                  disabled={rows.length === 1 || funcCode === 6}
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            )
          })}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button
              disabled={funcCode === 6}
              onClick={() => setRows((rs) => [...rs, defaultRow()])}
            >
              + Add type
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              total {nRegs} reg{nRegs === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        {/* Action button */}
        <Box sx={{ mb: 2 }}>
          <Button
            sx={op === 'write' ? { backgroundColor: 'error.dark' } : undefined}
            disabled={op === 'read' ? !canRunRead : !canRunWrite}
            onClick={run}
          >
            {pending ? '...' : (op === 'read' ? 'Read' : 'Write')}
          </Button>
        </Box>

        {/* History */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>Last operations</Typography>
          {history.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>(no operations yet)</Typography>}
          {history.map((h, i) => {
            const statusColor =
              h.status === 'success'
                ? 'success.main'
                : h.status === 'failure'
                  ? 'error.main'
                  : h.status === 'timeout' ? 'warning.main' : 'error.dark'
            return (
              <Box key={i} sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mb: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Box>{h.ts.toLocaleTimeString('fi-FI', { timeZone: 'Europe/Helsinki' })}</Box>
                <Box>{h.op.toUpperCase()}</Box>
                <Box>fc=0x{h.funcCode.toString(16).padStart(2, '0').toUpperCase()}</Box>
                <Box>reg={h.regaddr}</Box>
                <Box sx={{ color: statusColor, fontWeight: 700 }}>{h.status.toUpperCase()}</Box>
                {h.parsed.length > 0 && <Box>[ {h.parsed.join(', ')} ]</Box>}
              </Box>
            )
          })}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
